import { kml } from '@tmcw/togeojson';
import { unzipSync } from 'fflate';
import type { FeatureCollection, Geometry } from 'geojson';

/**
 * Error thrown when KML parsing fails.
 */
export class KMLParseError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'KMLParseError';
  }
}

/**
 * Converts KML content (string or ArrayBuffer) to a GeoJSON FeatureCollection.
 *
 * Uses @tmcw/togeojson to parse KML placemarks, styles, and folders into
 * GeoJSON features. Preserves feature geometries and attributes during conversion.
 *
 * @param data - KML content as a string or ArrayBuffer
 * @returns A GeoJSON FeatureCollection with all parsed features
 * @throws KMLParseError if the KML content is malformed or cannot be parsed
 */
export function toGeoJSON(data: string | ArrayBuffer): FeatureCollection {
  const kmlString = normalizeInput(data);

  if (!kmlString.trim()) {
    throw new KMLParseError('KML content is empty');
  }

  const doc = parseXML(kmlString);
  validateKMLDocument(doc);

  const featureCollection = kml(doc) as FeatureCollection<Geometry | null>;

  // Filter out features with null geometries and ensure clean output
  const cleanedFeatures = featureCollection.features
    .filter((feature) => feature.geometry !== null)
    .map((feature) => ({
      type: 'Feature' as const,
      geometry: feature.geometry!,
      properties: cleanProperties(feature.properties),
      ...(feature.id !== undefined ? { id: feature.id } : {}),
    }));

  return {
    type: 'FeatureCollection',
    features: cleanedFeatures,
  };
}

/**
 * Checks if a byte array starts with the ZIP magic bytes header (PK\x03\x04).
 */
function isZipArchive(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    bytes[2] === 0x03 &&
    bytes[3] === 0x04
  );
}

/**
 * Normalizes input data to a string.
 * Handles string, ArrayBuffer, and KMZ (zipped KML) inputs.
 */
function normalizeInput(data: string | ArrayBuffer): string {
  if (typeof data === 'string') {
    return data;
  }

  let bytes: Uint8Array | null = null;

  if (data instanceof ArrayBuffer) {
    bytes = new Uint8Array(data);
  } else if (ArrayBuffer.isView(data)) {
    bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  } else if (data && typeof data === 'object' && 'byteLength' in data) {
    bytes = new Uint8Array(data as ArrayBuffer);
  }

  if (bytes) {
    if (isZipArchive(bytes)) {
      try {
        const unzipped = unzipSync(bytes);

        // Search for .kml entry (e.g. doc.kml or any .kml file)
        const kmlFileName = Object.keys(unzipped).find((name) =>
          name.toLowerCase().endsWith('.kml')
        );

        if (!kmlFileName) {
          throw new KMLParseError('No KML file found in KMZ archive');
        }

        const decoder = new TextDecoder('utf-8');
        return decoder.decode(unzipped[kmlFileName]);
      } catch (error) {
        if (error instanceof KMLParseError) {
          throw error;
        }
        throw new KMLParseError(
          `Failed to extract KMZ archive: ${error instanceof Error ? error.message : String(error)}`,
          error
        );
      }
    }

    const decoder = new TextDecoder('utf-8');
    return decoder.decode(bytes);
  }

  throw new KMLParseError('Invalid input: expected string or ArrayBuffer');
}

/**
 * Parses a KML string into an XML Document.
 * Detects and reports XML parsing errors.
 */
function parseXML(kmlString: string): Document {
  const parser = new DOMParser();
  const doc = parser.parseFromString(kmlString, 'application/xml');

  // Check for XML parsing errors
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    const errorMessage = parseError.textContent || 'Unknown XML parsing error';
    throw new KMLParseError(`Invalid XML: ${errorMessage}`);
  }

  return doc;
}

/**
 * Validates that the parsed XML document is a KML document.
 * Checks for the presence of a <kml> root element.
 */
function validateKMLDocument(doc: Document): void {
  const root = doc.documentElement;

  if (!root) {
    throw new KMLParseError('KML document has no root element');
  }

  // Check for <kml> root element (case-insensitive local name check)
  const localName = root.localName || root.nodeName;
  if (localName.toLowerCase() !== 'kml') {
    throw new KMLParseError(
      `Expected <kml> root element, found <${localName}>. The file may not be a valid KML document.`
    );
  }
}

/**
 * Cleans feature properties by removing undefined values
 * and ensuring a consistent properties object.
 */
function cleanProperties(
  properties: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!properties) return {};

  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}
